<template>
  <div
    :class="[
      'search-autocomplete__suggestions',
      'ds-z-20',
      'ds-flex',
      'ds-flex-col',
      'ds-relative',
      'ds-bg-white',
      'ds-w-full',
      'ds-overflow-y-auto',
      'md:ds-shadow-md',
      'md:ds-rounded-md',
      'xl:ds-absolute',
    ]"
  >
    <slot name="header" />
    <div v-if="hasRecentSearchesChips || hasRecentBrandsChips">
      <div
        v-if="hasRecentSearchesChips"
        class="search-autocomplete__recent-chips"
      >
        <div class="search-autocomplete__recent-chips-label">
          {{ t('retailer.searchbar.section.recentSearches.lbl') }}
        </div>
        <div class="search-autocomplete__recent-chips-container">
          <AkTag
            v-for="item in results.history"
            :key="item.id"
            :label="item.name"
            closable
            size="xs"
            color="neutral"
            @close="handleChipRemove(item)"
            @click="handleChipClick(item)"
          />
        </div>
      </div>
      <div
        v-if="hasRecentBrandsChips"
        class="search-autocomplete__recent-brands"
        data-testid="SearchBoxRecentBrands"
      >
        <div class="search-autocomplete__recent-chips-label">
          {{ t('retailer.searchbar.section.recentBrands.lbl') }}
        </div>
        <div class="search-autocomplete__recent-chips-container">
          <div
            v-for="brand in results.recentBrands"
            :key="brand.id"
            class="search-autocomplete__brand-chip"
            @click="handleBrandChipClick(brand)"
          >
            <img
              class="search-autocomplete__brand-chip-image"
              :src="imgCdnUrl(brand.imageRounded, '24')"
              :alt="brand.name"
            >
            <span class="search-autocomplete__brand-chip-name">{{ brand.name }}</span>
            <button
              type="button"
              class="search-autocomplete__brand-chip-close"
              @click.stop="handleBrandChipRemove(brand)"
            >
              <AkIcon
                size="sm"
                symbol="x"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
    <CategoryTilesContainer v-else-if="shouldShowCategoryTiles">
      <template #default="{ categoriesTileData }">
        <CategoryTiles
          class="ds-p-4"
          :categories-tile-data="categoriesTileData"
          :columns="isMobile ? 2 : 4"
          @tile-clicked="handleCategoryTileClicked"
        />
      </template>
    </CategoryTilesContainer>
    <template v-else>
      <template
        v-for="key in dynamicKeys"
        :key="key"
      >
        <SearchAutocompleteSection
          v-if="results[key]?.length"
          :label="sectionLabels[key]"
          :type="key"
          :items="results[key]"
          :data-testid="SECTION_ATTRIBUTES[key]?.testId"
          :should-highlight-results="shouldHighlightResults"
        />
      </template>
    </template>
    <SearchStaticItem
      v-for="(_, name) in resultsStatic"
      :key="name"
      :class="{
        'search-autocomplete__suggestions-r2bLink': isR2BLink(results[name]),
      }"
      :is-visible="!!results[name]"
      :item="results[name]"
      @click="handleStaticItemClick"
    />
  </div>
</template>

<script setup lang="ts">
import SearchAutocompleteSection from './search-autocomplete-section.vue';
import SearchStaticItem from './search-autocomplete-static-item.vue';
import CategoryTilesContainer from './category-tiles-container.vue';
import { computed } from 'vue';
import { CategoryTiles } from '@bc/discovery/ui/category-tiles';
import { AkTag, AkIcon } from '@ankorstore/design-system';
import { useBreakpoints } from '@monolith/legacy/modules/design-system-candidates';
import { imgCdnUrl } from '@bc/shared/util/img-cdn/img-cdn';
import { R2B_LINK_ID, SECTION_ATTRIBUTES, SHOW_ALL_ID } from '../constants';
import { HANDLE_ITEM_CLICK, HANDLE_ITEM_CLICK_REMOVAL, BROWSE_TO_RESULT, HANDLE_RECENT_BRAND_REMOVAL } from '../store/action-types';
import { SET_ORIGINAL_USER_INPUT } from '../store/mutation-types';

import { useI18n } from 'vue-i18n';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';
import Analytics from '@monolith/legacy/services/analytics';
import UserClick from '@monolith/legacy/services/analytics/events/user-click.event';
import SearchBarStoreHelpers from '../store/helpers';
import { StaticItem, ItemResult, DynamicResults, RecentBrandItem } from '@bc/discovery/domain/search';

const { t } = useI18n();
const isFFEnabled = isEnabled(FeatureFlag.SearchRecentBrands);

const sectionLabels = computed<Partial<Record<string, string>>>(() =>
  isFFEnabled
    ? {
        brands: t('retailer.searchbar.section.brands.lbl'),
        categories: t('retailer.searchbar.section.categories.lbl'),
        boutiques: t('retailer.searchbar.section.boutiques.lbl'),
      }
    : {}
);

const emit = defineEmits<{
  'click-see-all-results': [];
  'click-r2b-link': [];
}>();

const { isMobile } = useBreakpoints();
const { results, resultsDynamic, resultsStatic, originalUserInput } = SearchBarStoreHelpers.useGetters([
  'results',
  'resultsDynamic',
  'resultsStatic',
  'originalUserInput',
]);
const {
  [HANDLE_ITEM_CLICK]: handleItemClick,
  [HANDLE_ITEM_CLICK_REMOVAL]: handleItemClickRemoval,
  [BROWSE_TO_RESULT]: browseToResult,
  [HANDLE_RECENT_BRAND_REMOVAL]: handleRecentBrandRemoval,
} = SearchBarStoreHelpers.useActions([HANDLE_ITEM_CLICK, HANDLE_ITEM_CLICK_REMOVAL, BROWSE_TO_RESULT, HANDLE_RECENT_BRAND_REMOVAL]);
const { [SET_ORIGINAL_USER_INPUT]: setOriginalUserInput } =
  SearchBarStoreHelpers.useMutations([SET_ORIGINAL_USER_INPUT]);

const hasRecentSearchesChips = computed(
  () => isFFEnabled && !originalUserInput.value && !!results.value.history?.length
);

const hasRecentBrandsChips = computed(
  () => isFFEnabled && !originalUserInput.value && !!results.value.recentBrands?.length
);

const shouldShowCategoryTiles = computed(() =>
  !Object.values(resultsDynamic.value).some(
    (val) => (Array.isArray(val) ? val.length : val)
  ) && !(isFFEnabled && results.value.recentBrands?.length)
);

const handleChipClick = (item: ItemResult) => {
  setOriginalUserInput(item.name);
  handleItemClick({ item });
};

const handleChipRemove = (item: ItemResult) => {
  handleItemClickRemoval({ item });
};

const handleBrandChipClick = (brand: RecentBrandItem) => {
  Analytics.track(
    new UserClick({
      component: 'search bar',
      action: 'click_recent_brand',
      brand_id: brand.reference,
    })
  );
  browseToResult({ href: brand.link, searchQuery: brand.name });
};

const handleBrandChipRemove = (brand: RecentBrandItem) => {
  handleRecentBrandRemoval({ item: brand });
};

const dynamicKeys = computed(() => Object.keys(resultsDynamic.value) as (keyof DynamicResults)[]);

const shouldHighlightResults = computed(() => Boolean(originalUserInput.value));

const isR2BLink = (item: StaticItem) => item?.id === R2B_LINK_ID;

const handleStaticItemClick = (item: StaticItem): void => {
  if (item?.id === SHOW_ALL_ID) {
    emit('click-see-all-results');
  } else if (isR2BLink(item)) {
    emit('click-r2b-link');
  }
};

const handleCategoryTileClicked = async (categoryId: number) => {
  await Analytics.track(
    new UserClick({
      component: 'search bar',
      action: 'click_lvl1_category_suggestion',
      id_category: categoryId || null,
    })
  );
};
</script>

<style scoped lang="scss">
.search-autocomplete__suggestions {
  min-height: 1rem;

  &-r2bLink {
    &:before {
      @apply ds-bg-neutral-300 ds-mx-4 ds-absolute;
      content: '';
      height: 1px;
      top: 0;
      left: -16px;
      right: -16px;
    }
  }
}

.search-autocomplete__recent-chips {
  @apply ds-px-4 ds-py-3;
}

.search-autocomplete__recent-brands {
  @apply ds-px-4 ds-py-3;
}

.search-autocomplete__recent-chips-label {
  @apply ds-text-left ds-text-sm ds-font-bold ds-mb-2;
}

.search-autocomplete__recent-chips-container {
  @apply ds-flex ds-flex-wrap ds-gap-2;

  :deep(.ak-tag--neutral) {
    @apply ds-bg-neutral-100;
  }
}

.search-autocomplete__brand-chip {
  @apply ds-inline-flex ds-items-center ds-gap-1 ds-px-2 ds-py-1 ds-rounded-lg ds-bg-neutral-100 ds-cursor-pointer ds-text-xs;
  min-height: 1.5rem;

  &:hover {
    @apply ds-bg-neutral-300;
  }
}

.search-autocomplete__brand-chip-image {
  @apply ds-rounded-full ds-border ds-border-solid ds-border-neutral-300;
  width: 20px;
  height: 20px;
}

.search-autocomplete__brand-chip-name {
  @apply ds-truncate;
  max-width: 150px;
}

.search-autocomplete__brand-chip-close {
  @apply ds-text-neutral-500 ds-flex ds-items-center;
}
</style>
